/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, MapPin, Save, Trash2, Plus } from 'react-feather';
import { MapContainer, TileLayer, AttributionControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import AuthService from '../../services/authService';
import { pickMapCenter } from './agentMapDefaults';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

/**
 * Leaflet expects a [lat, lng] center; GeoJSON polygons store [lng, lat].
 * Convert between them as needed at the edges; keep one convention per layer.
 */

// ─── Drawing layer ───────────────────────────────────────────────────────────
//
// Lives inside the MapContainer because react-leaflet's `useMap` is only
// available to descendants of the container. Owns a single editable
// FeatureGroup, attaches geoman to it, and notifies the parent whenever the
// drawn polygon changes.
//
// Props:
//   - initialPolygon  GeoJSON Polygon to render on mount (when picking a
//                     saved area). null/undefined = clean slate.
//   - onChange        called with a GeoJSON Polygon (or null when cleared).
const DrawLayer = ({ initialPolygon, onChange }) => {
  const map = useMap();
  const layerRef = useRef(null);

  // Stable ref to onChange so we don't re-init geoman on every render.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    if (!map) return;

    // Create the FeatureGroup that will hold the agent's polygon.
    const fg = new L.FeatureGroup();
    fg.addTo(map);
    layerRef.current = fg;

    // Configure geoman: polygon-only drawing, edit/drag/delete enabled.
    map.pm.addControls({
      position: 'topright',
      drawMarker: false,
      drawCircleMarker: false,
      drawPolyline: false,
      drawRectangle: false,
      drawCircle: false,
      drawText: false,
      drawPolygon: true,
      editMode: true,
      dragMode: true,
      cutPolygon: false,
      removalMode: true,
      rotateMode: false
    });
    map.pm.setLang('en');
    // Constrain to one polygon at a time — drawing a new one removes the old.
    // (Geoman doesn't have a built-in "single shape" mode; we enforce it on
    // the create event.)

    const emitCurrentPolygon = () => {
      const layers = fg.getLayers();
      if (layers.length === 0) {
        onChangeRef.current && onChangeRef.current(null);
        return;
      }
      const last = layers[layers.length - 1];
      const geo = last.toGeoJSON();
      // toGeoJSON returns a Feature; we only want the geometry.
      const polygon = geo?.geometry || null;
      if (polygon && polygon.type === 'Polygon') {
        onChangeRef.current && onChangeRef.current(polygon);
      } else {
        onChangeRef.current && onChangeRef.current(null);
      }
    };

    // When a polygon is created, remove any previous shapes first, then add
    // it to our FeatureGroup so geoman edit/remove tools target it.
    const handleCreate = (e) => {
      // Clear earlier shapes.
      fg.eachLayer(layer => {
        if (layer !== e.layer) fg.removeLayer(layer);
      });
      fg.addLayer(e.layer);
      // Remove the layer that was added at the map level by geoman; we
      // want it ONLY in our FeatureGroup.
      if (map.hasLayer(e.layer) && !fg.hasLayer(e.layer)) {
        map.removeLayer(e.layer);
        fg.addLayer(e.layer);
      }
      emitCurrentPolygon();
    };

    const handleEdit = () => emitCurrentPolygon();
    const handleRemove = () => emitCurrentPolygon();

    map.on('pm:create', handleCreate);
    map.on('pm:edit', handleEdit);
    map.on('pm:remove', handleRemove);

    // If we were handed an initial polygon (saved area), render it.
    if (initialPolygon && initialPolygon.type === 'Polygon') {
      const layer = L.geoJSON(initialPolygon).getLayers()[0];
      if (layer) {
        fg.addLayer(layer);
        // Fit map to the polygon bounds so the agent can see what they picked.
        try {
          map.fitBounds(layer.getBounds(), { padding: [40, 40] });
        } catch (_) { /* ignore if bounds invalid */ }
      }
    }

    return () => {
      map.off('pm:create', handleCreate);
      map.off('pm:edit', handleEdit);
      map.off('pm:remove', handleRemove);
      try { map.pm.removeControls(); } catch (_) {}
      try { fg.remove(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  return null;
};

// ─── Main modal ──────────────────────────────────────────────────────────────
//
// Props:
//   - isOpen         show/hide
//   - onClose        called when agent dismisses without picking
//   - onApply        called with { geometry, savedAreaId? } when agent confirms
//   - claimedCities  used to choose the default map center; pass searchForm.cities
//                    or availableCities from CurateMatchesModal
//   - currentPolygon optional — if the agent already had a polygon active in the
//                    parent, render it on open so they can edit instead of redraw
const MapAreaPickerModal = ({
  isOpen,
  onClose,
  onApply,
  claimedCities = [],
  currentPolygon = null
}) => {
  // Working polygon — what's currently drawn / picked. Sent to onApply on confirm.
  const [polygon, setPolygon] = useState(currentPolygon);
  // Whether the working polygon was loaded from a saved area (and which one).
  const [activeSavedAreaId, setActiveSavedAreaId] = useState(null);

  const [savedAreas, setSavedAreas] = useState([]);
  const [savedAreasLoading, setSavedAreasLoading] = useState(false);
  const [savedAreasError, setSavedAreasError] = useState('');

  // Save-this-area UI state.
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [newAreaName, setNewAreaName] = useState('');
  const [savingArea, setSavingArea] = useState(false);

  // Count preview state — shows "47 properties in this area" before applying.
  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewTimerRef = useRef(null);

  // Resolve initial center using the agent's claimed cities.
  // Memoized so the MapContainer's `center`/`zoom` don't change on re-renders
  // (Leaflet doesn't react to those props after mount anyway, but the
  // memoization makes intent clear).
  const initialView = useMemo(() => pickMapCenter(claimedCities), [claimedCities]);

  // Fetch the agent's saved areas on open.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        setSavedAreasLoading(true);
        setSavedAreasError('');
        const res = await fetch(`${API_URL}/saved-areas`, {
          headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
        });
        if (!res.ok) throw new Error('Failed to load saved areas');
        const json = await res.json();
        if (!cancelled) setSavedAreas(Array.isArray(json.data) ? json.data : []);
      } catch (err) {
        if (!cancelled) setSavedAreasError('Could not load your saved areas');
      } finally {
        if (!cancelled) setSavedAreasLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  // Reset internal state every time the modal opens fresh.
  useEffect(() => {
    if (isOpen) {
      setPolygon(currentPolygon);
      setActiveSavedAreaId(null);
      setShowSaveInput(false);
      setNewAreaName('');
      setPreviewCount(null);
    }
    // We deliberately ignore currentPolygon changes after open — it's a "seed".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Debounced count preview. Each polygon change schedules a /search call
  // with no other filters and a tiny limit, just to get the count.
  // We only want the COUNT but the endpoint returns properties; we cap to
  // 1 and trust the `count`/`capped` response. (We can't easily ask for
  // count-only without a new endpoint, and a HEAD-style call isn't worth
  // building for v1.)
  useEffect(() => {
    if (!polygon) {
      setPreviewCount(null);
      setPreviewLoading(false);
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
      return;
    }
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    setPreviewLoading(true);
    previewTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/saved-areas/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${AuthService.getToken()}`
          },
          body: JSON.stringify({ geometry: polygon, limit: 500 })
        });
        if (!res.ok) throw new Error('preview failed');
        const json = await res.json();
        // The response caps results at 500. If we hit that, show "500+".
        setPreviewCount({ n: json.count || 0, capped: !!json.capped });
      } catch (_) {
        setPreviewCount(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 500);
    return () => { if (previewTimerRef.current) clearTimeout(previewTimerRef.current); };
  }, [polygon]);

  if (!isOpen) return null;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handlePickSavedArea = (id) => {
    if (!id) {
      // "— New area —" picked: clear current polygon entirely.
      setActiveSavedAreaId(null);
      setPolygon(null);
      return;
    }
    const area = savedAreas.find(a => a._id === id);
    if (!area) return;
    setActiveSavedAreaId(id);
    setPolygon(area.geometry);
    setShowSaveInput(false);
  };

  const handleSaveArea = async () => {
    if (!polygon || !newAreaName.trim()) return;
    try {
      setSavingArea(true);
      const res = await fetch(`${API_URL}/saved-areas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ name: newAreaName.trim(), geometry: polygon })
      });
      if (!res.ok) throw new Error('Save failed');
      const json = await res.json();
      const created = json.data;
      setSavedAreas(prev => [created, ...prev]);
      setActiveSavedAreaId(created._id);
      setShowSaveInput(false);
      setNewAreaName('');
    } catch (err) {
      alert('Could not save this area. Please try again.');
    } finally {
      setSavingArea(false);
    }
  };

  const handleDeleteSavedArea = async (id) => {
    if (!window.confirm('Delete this saved area?')) return;
    try {
      const res = await fetch(`${API_URL}/saved-areas/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      if (!res.ok) throw new Error('Delete failed');
      setSavedAreas(prev => prev.filter(a => a._id !== id));
      if (activeSavedAreaId === id) {
        setActiveSavedAreaId(null);
        setPolygon(null);
      }
    } catch (_) {
      alert('Could not delete this area.');
    }
  };

  const handleApply = () => {
    if (!polygon) return;
    onApply && onApply({ geometry: polygon, savedAreaId: activeSavedAreaId });
    onClose && onClose();
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  const previewLabel = (() => {
    if (!polygon) return 'Draw a polygon to see how many properties are in the area.';
    if (previewLoading) return 'Counting properties…';
    if (previewCount == null) return 'Could not count properties in this area.';
    const { n, capped } = previewCount;
    if (capped) return `500+ properties in this area`;
    return `${n} ${n === 1 ? 'property' : 'properties'} in this area`;
  })();

  return createPortal(
    <>
      <style>{`
        .map-picker-overlay {
          position: fixed; inset: 0;
          background-color: rgba(0,0,0,0.42);
          backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000010;
          padding: 24px;
        }
        .map-picker {
          background: #ffffff;
          border-radius: 14px;
          width: 100%; max-width: 1080px;
          height: 88vh;
          display: flex; flex-direction: column;
          box-shadow: 0 32px 80px rgba(0,0,0,0.18);
          overflow: hidden;
          border: 1px solid rgba(26,26,26,0.08);
        }
        .map-picker-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px 22px;
          border-bottom: 1px solid rgba(26,26,26,0.08);
          flex-shrink: 0;
        }
        .map-picker-title {
          display: flex; align-items: center; gap: 8px;
          font-size: 0.95rem; font-weight: 600; color: #111;
        }
        .map-picker-close {
          background: none; border: none; color: rgba(26,26,26,0.5);
          padding: 6px; border-radius: 6px; cursor: pointer; display: flex;
          transition: color 0.15s;
        }
        .map-picker-close:hover { color: #111; }
        .map-picker-controls {
          display: flex; align-items: center; gap: 10px;
          padding: 12px 22px;
          background: #f8f7f4;
          border-bottom: 1px solid rgba(26,26,26,0.08);
          flex-shrink: 0; flex-wrap: wrap;
        }
        .map-picker-saved-select {
          flex: 0 1 auto; min-width: 220px;
          padding: 7px 10px;
          font-size: 0.82rem;
          border: 1px solid rgba(26,26,26,0.15);
          border-radius: 6px;
          background: #fff; color: #111;
          cursor: pointer;
        }
        .map-picker-btn {
          padding: 7px 12px; font-size: 0.78rem; font-weight: 600;
          border-radius: 6px; cursor: pointer;
          display: inline-flex; align-items: center; gap: 6px;
          transition: background-color 0.15s, color 0.15s;
        }
        .map-picker-btn-secondary {
          background: #fff; color: #111; border: 1px solid rgba(26,26,26,0.15);
        }
        .map-picker-btn-secondary:hover:not(:disabled) {
          background: #f0efeb;
        }
        .map-picker-btn-primary {
          background: #6366f1; color: #fff; border: 1px solid #6366f1;
        }
        .map-picker-btn-primary:hover:not(:disabled) { background: #4f52d8; border-color: #4f52d8; }
        .map-picker-btn-danger {
          background: #fff; color: #b91c1c; border: 1px solid rgba(185,28,28,0.3);
          padding: 5px 8px;
        }
        .map-picker-btn-danger:hover:not(:disabled) { background: #fef2f2; }
        .map-picker-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .map-picker-spacer { flex: 1; }
        .map-picker-saved-name-input {
          padding: 7px 10px; font-size: 0.82rem;
          border: 1px solid rgba(26,26,26,0.15); border-radius: 6px;
          background: #fff; color: #111;
          min-width: 180px;
        }
        .map-picker-map {
          flex: 1; min-height: 0; position: relative;
        }
        .map-picker-map .leaflet-container {
          width: 100%; height: 100%;
        }
        .map-picker-footer {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px;
          padding: 14px 22px;
          border-top: 1px solid rgba(26,26,26,0.08);
          flex-shrink: 0; background: #fff;
        }
        .map-picker-preview {
          font-size: 0.82rem; color: rgba(26,26,26,0.65);
          display: flex; align-items: center; gap: 8px;
        }
        .map-picker-preview-strong { color: #111; font-weight: 600; }
        .map-picker-error {
          font-size: 0.78rem; color: #b91c1c;
          padding: 0 22px 8px;
        }
        @media (max-width: 720px) {
          .map-picker { height: 92vh; max-width: 100%; }
          .map-picker-controls { padding: 10px 14px; }
          .map-picker-saved-select { min-width: 0; flex: 1; }
        }
      `}</style>

      <div className="map-picker-overlay" onClick={onClose}>
        <div className="map-picker" onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div className="map-picker-header">
            <div className="map-picker-title">
              <MapPin size={15} /> Draw a search area
            </div>
            <button className="map-picker-close" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>

          {/* Controls bar */}
          <div className="map-picker-controls">
            <select
              className="map-picker-saved-select"
              value={activeSavedAreaId || ''}
              onChange={(e) => handlePickSavedArea(e.target.value)}
              disabled={savedAreasLoading}
            >
              <option value="">
                {savedAreasLoading
                  ? 'Loading saved areas…'
                  : savedAreas.length === 0
                    ? 'No saved areas'
                    : '— Pick a saved area —'}
              </option>
              {savedAreas.map(a => (
                <option key={a._id} value={a._id}>{a.name}</option>
              ))}
            </select>

            {activeSavedAreaId && (
              <button
                className="map-picker-btn map-picker-btn-danger"
                onClick={() => handleDeleteSavedArea(activeSavedAreaId)}
                title="Delete this saved area"
              >
                <Trash2 size={12} /> Delete
              </button>
            )}

            <div className="map-picker-spacer" />

            {/* Save-this-area: only meaningful when a polygon exists AND it isn't already saved */}
            {polygon && !activeSavedAreaId && !showSaveInput && (
              <button
                className="map-picker-btn map-picker-btn-secondary"
                onClick={() => setShowSaveInput(true)}
              >
                <Plus size={12} /> Save this area
              </button>
            )}
            {showSaveInput && (
              <>
                <input
                  type="text"
                  className="map-picker-saved-name-input"
                  placeholder="e.g. Aurora SE"
                  value={newAreaName}
                  onChange={e => setNewAreaName(e.target.value)}
                  maxLength={80}
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveArea(); }}
                />
                <button
                  className="map-picker-btn map-picker-btn-primary"
                  onClick={handleSaveArea}
                  disabled={!newAreaName.trim() || savingArea}
                >
                  <Save size={12} /> {savingArea ? 'Saving…' : 'Save'}
                </button>
                <button
                  className="map-picker-btn map-picker-btn-secondary"
                  onClick={() => { setShowSaveInput(false); setNewAreaName(''); }}
                  disabled={savingArea}
                >
                  Cancel
                </button>
              </>
            )}
          </div>

          {savedAreasError && (
            <div className="map-picker-error">{savedAreasError}</div>
          )}

          {/* Map */}
          <div className="map-picker-map">
            <MapContainer
              center={initialView.center}
              zoom={initialView.zoom}
              scrollWheelZoom
              attributionControl={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                subdomains="abcd"
                maxZoom={19}
              />
              {/* Custom attribution control: same OSM/CARTO credits the
                  TileLayer registers, but with the "Leaflet" prefix suppressed.
                  Allowed under both licenses — only OSM data attribution and
                  CARTO style attribution are required. */}
              <AttributionControl position="bottomright" prefix={false} />
              <DrawLayer
                initialPolygon={polygon}
                onChange={(p) => {
                  // When the agent edits/clears the polygon manually, the
                  // saved-area "link" is broken — they're working on a fresh
                  // shape now.
                  setActiveSavedAreaId(null);
                  setPolygon(p);
                }}
              />
            </MapContainer>
          </div>

          {/* Footer */}
          <div className="map-picker-footer">
            <div className="map-picker-preview">
              <span className="map-picker-preview-strong">{previewLabel}</span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="map-picker-btn map-picker-btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="map-picker-btn map-picker-btn-primary"
                onClick={handleApply}
                disabled={!polygon}
              >
                Use this area
              </button>
            </div>
          </div>

        </div>
      </div>
    </>,
    document.body
  );
};

export default MapAreaPickerModal;